/**
 * PyG analítico — modelos de negocio simplificados para la UI.
 *
 * Dos ejes en contabilidad (no confundir):
 * - Sociedad: STAGE_DESIGN vs ERNA_TRADE
 * - División Nivel1: SDS, NML, General (dentro de la vista PyG de Stage)
 *
 * Stage (SDS) y NML (Nivel1) comparten STAGE_DESIGN; ERNA es otra sociedad.
 * NML_SPAIN es otra sociedad A3 (BD propia), no la división NML de Stage.
 */

/** Vista por defecto al entrar en PyG analítico (global y por marca). */
export const DEFAULT_PYG_VISTA = "fusion";

/** Vistas unificadas para PyG global y por marca */
export const PYG_VISTA_OPTIONS = [
  {
    value: "stage",
    label: "STAGE (SDS)",
    apiEmpresa: "stage",
    apiNivel1: "SDS",
    omitirGeneralStage: false,
  },
  {
    value: "nml",
    label: "NML",
    apiEmpresa: "stage",
    apiNivel1: "NML",
    omitirGeneralStage: false,
  },
  {
    value: "general",
    label: "GENERAL",
    apiEmpresa: "stage",
    apiNivel1: "General",
    omitirGeneralStage: false,
  },
  {
    value: "fusion_sds_nml",
    label: "FUSIÓN SDS/NML/GENERAL",
    apiEmpresa: "stage",
    apiNivel1: null,
    omitirGeneralStage: false,
  },
  {
    value: "erna",
    label: "ERNA",
    apiEmpresa: "erna_trade",
    apiNivel1: null,
    omitirGeneralStage: false,
  },
  {
    value: "fusion",
    label: "FUSIÓN TOTAL (SDS/NML/GENERAL + ERNA)",
    apiEmpresa: "fusion",
    apiNivel1: null,
    omitirGeneralStage: false,
  },
];

/** Vista bloqueada de la pestaña NML PyG (sociedad NML_SPAIN). No va en el selector de Finanzas. */
export const PYG_NML_SPAIN_VISTA = {
  value: "nml_spain",
  label: "NML SPAIN",
  apiEmpresa: "nml_spain",
  apiNivel1: null,
  omitirGeneralStage: false,
};

/** Vista global PyG (tabla única, sin filtro SDS/NML) */
export const PYG_EMPRESA_OPTIONS = [
  { value: "fusion", label: "Fusión (Stage + ERNA)", apiValue: "fusion" },
  { value: "stage", label: "STAGE_DESIGN", apiValue: "stage" },
  { value: "erna_trade", label: "ERNA_TRADE", apiValue: "erna_trade" },
];

export const normalizePygVista = (raw) => {
  if (raw == null || String(raw).trim() === "") return DEFAULT_PYG_VISTA;
  const v = String(raw).trim().toLowerCase().replace(/-/g, "_");
  if (v === "stage" || v === "stage_design" || v === "sds") return "stage";
  if (v === "nml") return "nml";
  if (v === "nml_spain" || v === "nmlspain") return "nml_spain";
  if (v === "general") return "general";
  if (v === "erna" || v === "erna_trade") return "erna";
  if (v === "fusion") return "fusion";
  if (
    v === "fusion_sds_nml" ||
    v === "fusion_sdsnml" ||
    v === "fusion_stage" ||
    v === "fusion_stage_total"
  )
    return "fusion_sds_nml";
  return DEFAULT_PYG_VISTA;
};

/** Ruta empresa en PyG global (/pyg-analitico/:empresa) */
export const normalizePygEmpresaRoute = (raw) => {
  if (raw == null || String(raw).trim() === "") return "fusion";
  const v = String(raw).trim().toLowerCase().replace(/-/g, "_");
  if (
    v === "stage" ||
    v === "stage_design" ||
    v === "sds" ||
    v === "nml" ||
    v === "general" ||
    v === "fusion_sds_nml"
  )
    return "stage";
  if (v === "erna" || v === "erna_trade") return "erna_trade";
  if (v === "nml_spain" || v === "nmlspain") return "nml_spain";
  if (v === "fusion") return "fusion";
  return "fusion";
};

export const isNmlSpainPygVista = (raw) =>
  normalizePygVista(raw) === "nml_spain";

/** Acceso a una vista PyG: NML_SPAIN exige Permiso_NMLFinanzas; el resto Permiso_PyG. */
export const canAccessPygVista = (permissions = {}, vista) => {
  if (isNmlSpainPygVista(vista)) {
    return Boolean(permissions.Permiso_NMLFinanzas);
  }
  return Boolean(permissions.Permiso_PyG);
};

export const resolvePygVista = (raw) => {
  const v = normalizePygVista(raw);
  if (v === "nml_spain") return PYG_NML_SPAIN_VISTA;
  return (
    PYG_VISTA_OPTIONS.find((o) => o.value === v) ??
    PYG_VISTA_OPTIONS.find((o) => o.value === DEFAULT_PYG_VISTA)
  );
};

/** Valor para ?empresa= en API PyG (vista global) */
export const pygEmpresaApiParam = (routeOrApi) => {
  const v = normalizePygEmpresaRoute(routeOrApi);
  const opt = PYG_EMPRESA_OPTIONS.find((o) => o.value === v);
  return opt?.apiValue ?? v;
};

export const getPygEmpresaLabel = (routeOrApi) => {
  const v = normalizePygEmpresaRoute(routeOrApi);
  return (
    PYG_EMPRESA_OPTIONS.find((o) => o.value === v)?.label ?? v.toUpperCase()
  );
};

export const getPygVistaApiParams = (vista) => {
  const cfg = resolvePygVista(vista);
  return {
    empresa: cfg.apiEmpresa,
    nivel1: cfg.apiNivel1 || undefined,
    omitirGeneralStage: cfg.omitirGeneralStage || undefined,
  };
};

export const buildPygMarcasUrl = (apiUrl, year, vista) => {
  const { empresa, nivel1, omitirGeneralStage } = getPygVistaApiParams(vista);
  let url = `${apiUrl}/api/pyg/marcas-npgc?year=${year}&empresa=${encodeURIComponent(empresa)}`;
  if (nivel1) url += `&nivel1=${encodeURIComponent(nivel1)}`;
  if (omitirGeneralStage) url += "&omitirGeneralStage=1";
  return url;
};

/** Texto bajo el filtro en por-marca */
export const getPygFiltroResumen = (vista) => {
  const cfg = resolvePygVista(vista);
  switch (cfg.value) {
    case "stage":
      return "PyG de STAGE_DESIGN · división SDS (pedidos venta: solo filas V.SDS='SDS', sin NML).";
    case "nml":
      return "PyG de STAGE_DESIGN · división NML (misma sociedad Stage, otro circuito).";
    case "general":
      return "PyG de STAGE_DESIGN · división GENERAL.";
    case "erna":
      return "PyG de ERNA_TRADE. Venta/Compra = facturas contables (epígr. 1 y 4). Pedidos por CC2. Facturas ERNA sin marca en Nivel2 van en General.";
    case "fusion":
      return "Fusión total: Stage + ERNA. Los pedidos venta incluyen también ERNA_TRADE. Mes de entrega (MexCEX), no de cobro.";
    case "fusion_sds_nml":
      return "Stage (SDS + NML + GENERAL), sin ERNA. Pedidos/proyecciones por mes de entrega — misma vista A3 V_Carlos_Ped_Venta.";
    case "nml_spain":
      return "PyG de NML_SPAIN (sociedad A3 propia, no la división NML de Stage).";
    default:
      return "";
  }
};

export const getPygFiltroChips = (vista, year) => {
  const cfg = resolvePygVista(vista);
  return [
    { key: "year", label: `Año ${year}` },
    { key: "vista", label: cfg.label },
  ];
};

export const pygAnaliticoBasePath = (vista, porMarca = false) => {
  const v = normalizePygVista(vista);
  const allowPorMarca = porMarca && !isNmlSpainPygVista(v);
  const suffix = allowPorMarca ? "/por-marca" : "";
  const segment =
    v === "erna" ? "erna_trade" : v === "fusion_sds_nml" ? "fusion_sds_nml" : v;
  return `/pyg-analitico/${encodeURIComponent(segment)}${suffix}`;
};

export { NML_BALANCE_PATH } from "./balanceNpgcEstructura";

export const pygVistaFallbackPath = (permissions = {}, porMarca = false) => {
  if (permissions.Permiso_NMLFinanzas) {
    return pygAnaliticoBasePath("nml_spain", porMarca);
  }
  if (permissions.Permiso_PyG) {
    return pygAnaliticoBasePath(DEFAULT_PYG_VISTA, porMarca);
  }
  return "/";
};
